import styled from 'styled-components';
import { observer } from 'mobx-react';
import { Menu } from 'core/components';
import { SortableContainer, SortEndHandler } from 'react-sortable-hoc';
import { MetricOrDimensionItem } from 'app/views/boards/MetricOrDimensionItem';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Measurement, RollupFunction } from '@kentik/ui-shared/query/query.types';
import Container from 'core/components/Container';
import { getAnimationStyle, ANIMATION_DURATION_MS } from './selectionAnimations';

const WrappedMenu = styled(Menu)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export interface SortableMenuProps {
  measurement: string;
  handleRemoveItem: (name: string) => void;
  type: 'dimension' | 'metric';
  measurements: Measurement[];
  // used by SortableContainer but via janky HOC pattern that doesn't declare props correctly
  // eslint-disable-next-line react/no-unused-prop-types
  onSortEnd: SortEndHandler;

  items: { name: string }[];
  getRollupFns?: (metricName: string) => RollupFunction[];
  onRollupChange?: (metricName: string, fns: RollupFunction[]) => void;
  customPercentiles?: RollupFunction[];
}

interface AnimatedSortableProps extends Omit<SortableMenuProps, 'handleRemoveItem'> {
  enteringItems: Set<string>;
  exitingItems: Set<string>;
  onAnimatedRemove: (name: string) => void;
}

const SortableInnerContainer = ({
  items,
  getRollupFns,
  onRollupChange,
  measurement,
  onAnimatedRemove,
  type,
  measurements,
  enteringItems,
  exitingItems,
  customPercentiles
}: AnimatedSortableProps) => (
  <WrappedMenu bg="transparent" p={0}>
    {items.map((item, index) => (
      <MetricOrDimensionItem
        index={index}
        reorderable
        key={item.name}
        measurement={measurement}
        value={item.name}
        type={type}
        onRemove={() => {
          onAnimatedRemove(item.name);
        }}
        rollupFns={getRollupFns ? getRollupFns(item.name) : undefined}
        onRollupChange={onRollupChange ? (fns) => onRollupChange(item.name, fns) : undefined}
        customPercentiles={customPercentiles}
        measurements={measurements}
        isEntering={enteringItems.has(item.name)}
        isExiting={exitingItems.has(item.name)}
      />
    ))}
  </WrappedMenu>
);

// Wrap with observer to ensure MobX reactivity propagates through SortableContainer
const InnerContainer = SortableContainer(observer(SortableInnerContainer));

export const SortableMenu = ({
  onSortEnd,
  getRollupFns,
  onRollupChange,
  customPercentiles,
  handleRemoveItem,
  items,
  ...props
}: SortableMenuProps) => {
  const prevItemsRef = useRef<Set<string>>(new Set(items.map((i) => i.name)));
  const [enteringItems, setEnteringItems] = useState<Set<string>>(new Set());
  const [exitingItems, setExitingItems] = useState<Set<string>>(new Set());
  const removeTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Clear all remove timers on unmount to avoid state updates after unmount
  useEffect(
    () => () => {
      removeTimersRef.current.forEach((id) => clearTimeout(id));
      removeTimersRef.current.clear();
    },
    []
  );

  // Detect newly added items
  useEffect(() => {
    const style = getAnimationStyle();
    if (style === 'none') {
      prevItemsRef.current = new Set(items.map((i) => i.name));
      return undefined;
    }

    const currentNames = new Set(items.map((i) => i.name));
    const newItems = new Set<string>();

    for (const name of currentNames) {
      if (!prevItemsRef.current.has(name)) {
        newItems.add(name);
      }
    }

    prevItemsRef.current = currentNames;

    if (newItems.size > 0) {
      setEnteringItems(newItems);
      const id = setTimeout(() => setEnteringItems(new Set()), ANIMATION_DURATION_MS);
      return () => clearTimeout(id);
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Animated remove: play exit animation, then call real handler
  const onAnimatedRemove = useCallback(
    (name: string) => {
      const style = getAnimationStyle();
      if (style === 'none') {
        handleRemoveItem(name);
        return;
      }

      setExitingItems((prev) => new Set(prev).add(name));
      const existing = removeTimersRef.current.get(name);
      if (existing !== undefined) {
        clearTimeout(existing);
      }
      removeTimersRef.current.set(
        name,
        setTimeout(() => {
          removeTimersRef.current.delete(name);
          setExitingItems((prev) => {
            const next = new Set(prev);
            next.delete(name);
            return next;
          });
          handleRemoveItem(name);
        }, ANIMATION_DURATION_MS)
      );
    },
    [handleRemoveItem]
  );

  return (
    <Container width="100%" useDragHandle {...props}>
      <InnerContainer
        useDragHandle
        onSortEnd={onSortEnd}
        getRollupFns={getRollupFns}
        onRollupChange={onRollupChange}
        customPercentiles={customPercentiles}
        onAnimatedRemove={onAnimatedRemove}
        items={items}
        enteringItems={enteringItems}
        exitingItems={exitingItems}
        {...props}
      />
    </Container>
  );
};
